import { useCallback, useEffect, useRef, useState, lazy, Suspense } from 'react';
import { useUIStore } from '@/state/store';
import type { NfcReadPayload } from '@/state/store';
import { playSound } from '@/hooks/useAudio';
import { useWebRtc } from '@/hooks/useWebRtc';
import { DeviceMenu } from '@/components/DeviceMenu';
import { TranslationMenu } from '@/components/TranslationMenu';
import { BackgroundMenu } from '@/components/BackgroundMenu';
import { toggleBg, getBgConfig } from '@/lib/virtualBackground';
import { getPreferredDevice, setPreferredDevice } from '@/lib/mediaDevices';
import { SelfPreviewModal } from '@/components/CameraPreview';
import { EncryptionBadge } from '@/components/EncryptionPanel';
import { useCallRecording, topicRequiresRecording } from '@/hooks/useCallRecording';
import { fetchRecordingPolicy, policyAllowsTopic, getTopic, enabledTopics, deliveryAdapterFor, DEFAULT_POLICY, type RecordingPolicy } from '@/lib/recordingPolicy';
import { apiFetch } from '@/lib/auth';
import { clearCallSession } from '@/lib/callSession';
import { captureFromUrl, captureBlob } from '@/lib/localDocs';
import { buildEcProtocolPdf } from '@/lib/ecPdf';
import { stampForExport } from '@/lib/docMeta';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AudioLevelMeter } from '@/components/AudioLevelMeter';
import { setBusylight, dialFeatureCode, LS_BUSYLIGHT } from '@/lib/headsetTelephony';
import type { Signer, SignMarker } from '@/lib/signTypes';
const SignPlacementModal = lazy(() => import('@/components/SignPlacementModal').then((m) => ({ default: m.SignPlacementModal }))); // pre224
import { saveToDir, saveWithDialog, getDirName, type TargetKind } from '@/lib/fileTargets';
import { SignInbox } from '@/components/SignInbox';
import { NfcBox } from '@/components/SidePanel';
import { useDocumentPip } from '@/hooks/useDocumentPip';
import { RecordingConsentModal } from '@/components/call/RecordingConsentModal';

const getActiveCallId = (): string | null =>
  (useUIStore.getState() as any).activeCallId ?? (window as any).__activeCallId ?? null;

function useCallTimer() {
  const [secs, setSecs] = useState(0); // pre245: startet bei 0:00 (222 war ein Demo-Relikt)
  useEffect(() => {
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const m = Math.floor(secs / 60);
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function CaptionOverlay() {
  const expanded = useUIStore((s) => s.captionExpanded);
  const toggleExpand = useUIStore((s) => s.toggleCaptionExpanded);
  const toggleTranslation = useUIStore((s) => s.toggleTranslation);

  return (
    <>
      <div className="cap-header">
        <span className="cap-header-label">🌐 TR ↔ DE · LIVE</span>
        <button className="cap-header-btn" onClick={toggleExpand} title={expanded ? 'Verkleinern' : 'Vergrößern'}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </button>
        <button className="cap-header-btn" onClick={toggleTranslation} title="Übersetzung aus">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="caption-overlay">
        <div className="cap-line from-customer live">
          <div className="cap-meta">Maria · TR → DE</div>
          Eğer mümkünse hemen hesap açabilir miyiz
          <span className="cap-typing">
            <span></span>
            <span></span>
            <span></span>
          </span>
        </div>
        <div className="cap-line from-customer">
          <div className="cap-meta">
            Maria · TR → DE <span className="cap-meta-warn">⚠ niedrige Confidence</span>
          </div>
          <span className="cap-very-low-conf">Pasaport</span> getirdim ama <span className="cap-low-conf">kimlik</span>{' '}
          evde unuttum.
        </div>
        <div className="cap-line from-self">
          <div className="cap-meta">Du · DE → TR</div>
          Sehr gerne. Haben Sie heute Ihren Ausweis dabei?
        </div>
        <div className="cap-line from-customer">
          <div className="cap-meta">Maria · TR → DE</div>
          Guten Tag, ich möchte ein Konto eröffnen.
        </div>
        <div className="cap-line from-self">
          <div className="cap-meta">Du · DE → TR</div>
          Hallo Frau König, schön dass Sie da sind.
        </div>
      </div>
    </>
  );
}

export function InCall() {
  const docFocused = useUIStore((s) => s.docFocused);
  const swapCameras = useUIStore((s) => s.swapCameras);
  const translationOn = useUIStore((s) => s.translationOn);
  const toggleTranslation = useUIStore((s) => s.toggleTranslation);
  const captionExpanded = useUIStore((s) => s.captionExpanded);
  const showTurnWarn = useUIStore((s) => s.showTurnWarn);
  const incoming = useUIStore((s) => s.incomingCall);
  const partnerLabel = [incoming?.steleName, incoming?.steleLocation].filter(Boolean).join(' · ') || 'Gast';

  // pre88: Document Picture-in-Picture — Partnerbild bleibt beim Fensterwechsel sichtbar.
  const pip = useDocumentPip();
  const [bgActive, setBgActive] = useState<boolean>(getBgConfig().mode !== 'none');
  const micMuted = useUIStore((s) => s.micMuted);
  const camOn = useUIStore((s) => s.camOn);
  const toggleMic = useUIStore((s) => s.toggleMic);
  const toggleCam = useUIStore((s) => s.toggleCam);

  const setView = useUIStore((s) => s.setView);
  const audioPrefs = useUIStore((s) => s.audioPrefs);
  const openEscalate = useUIStore((s) => s.openEscalate);
  const openTransfer = useUIStore((s) => s.openTransfer);
  // pre164: Verbindungsqualität (schlechtester der beiden Strecken) für das Live-Badge.
  const statsAgent = useUIStore((s) => s.callStatsAgent);
  const statsStele = useUIStore((s) => s.callStatsStele);
  const rttHistoryAgent = useUIStore((s) => s.rttHistoryAgent);
  const rttHistoryStele = useUIStore((s) => s.rttHistoryStele);
  const timer = useCallTimer();
  // pre251: Zero-Install-Rückkanal — beim Betreten des Calls Busylight an + optional
  // AUX-Code am Softphone wählen; beim Verlassen zurück (Busylight aus + READY-Code).
  useEffect(() => {
    const busylight = localStorage.getItem(LS_BUSYLIGHT) === '1';
    if (busylight) void setBusylight(true);
    dialFeatureCode('aux');
    return () => {
      if (busylight) void setBusylight(false);
      dialFeatureCode('ready');
    };
  }, []);

  // RTC: Token aus UIStore oder window (Fallback wenn Accept noch nicht im Store war)
  const rtcTokenFromStore = useUIStore((s) => s.rtcToken);
  const rtcToken = rtcTokenFromStore ?? (window as any).__rtcToken ?? null;
  useEffect(() => {
    console.log('[incall] mount, rtcToken vorhanden=', Boolean(rtcToken), 'fromStore=', Boolean(rtcTokenFromStore));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // pre98: Zugriff auf recorder.stop aus dem useWebRtc-Callback (recorder wird erst danach erzeugt).
  const recStopRef = useRef<(() => Promise<void>) | null>(null);
  const rtc = useWebRtc({
    rtcToken,
    role: 'agent',
    onPeerGone: async (reason) => {
      console.log('[incall] peer gone:', reason);
      // pre98: laufende Aufzeichnung sauber finalisieren (Paket bauen + ablegen) BEVOR der Kontext gelöscht wird.
      try { await recStopRef.current?.(); } catch { /* */ }
      // Stele weg → Call endgültig vorbei: F5-Kontext löschen und in Wrap-View springen
      clearCallSession();
      (window as any).__f5Restoring = false;
      setView('wrap');
    },
  });

  // Welle 12 (pre118/120): Companion-Remote (Variante A) — Auflegen/Mute/Kamera/Experte-trennen
  // fernsteuern und den eigenen A/V-Status an die Companion-Sitzung spiegeln. Nach der
  // rtc-Deklaration platziert, damit rtc verfügbar ist.
  useEffect(() => {
    const onRemote = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const action = detail?.action;
      if (action === 'hangup') void endCall();
      else if (action === 'mute') toggleMic();
      else if (action === 'camera') toggleCam();
      else if (action === 'kick-guest') rtc.disconnectGuest();
      else if (action === 'camera-swap') rtc.requestSwap();
      else if (action === 'rec-start') triggerRecordingStart(detail?.topic);
      else if (action === 'rec-stop') { if (recorder.recording) void recorder.stop(); }
    };
    window.addEventListener('bamoa:remote-cmd', onRemote);
    return () => window.removeEventListener('bamoa:remote-cmd', onRemote);
  });
  useEffect(() => () => { window.dispatchEvent(new CustomEvent('bamoa:remote-state', { detail: { inCall: false } })); }, []);
  // pre88c: gemeinsamer Options-Builder (manuelles + Auto-PiP) inkl. Swap/Scan/Signieren.
  const buildPipOptions = useCallback(() => ({
    stream: rtc.remoteStreamMain,
    micMuted,
    onToggleMic: () => toggleMic(),
    onHangup: () => endCall(),
    partnerLabel,
    onSwap: () => triggerSwap(),
    onScan: () => triggerSnapshot(),
    onSignFile: (file: File) => {
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      if (!isPdf) { console.warn('[pip] nur PDF-Dateien können signiert werden'); return; }
      openPlacement(file);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [rtc.remoteStreamMain, micMuted, partnerLabel]);

  const togglePip = useCallback(() => {
    if (pip.active) { pip.close(); return; }
    void pip.open(buildPipOptions());
  }, [pip, buildPipOptions]);
  useEffect(() => { if (pip.active) pip.setStream(rtc.remoteStreamMain); }, [pip, rtc.remoteStreamMain]);
  useEffect(() => { if (pip.active) pip.syncMic(micMuted); }, [pip, micMuted]);

  // pre88b: Auto-PiP. Chrome ruft den 'enterpictureinpicture'-MediaSession-Handler beim
  // Wechsel weg von einem Kamera-/Mikrofon-Tab MIT Nutzeraktivierung auf → requestWindow
  // ist dann erlaubt. Beim Zurückkehren (Tab wieder sichtbar) schließen wir das PiP.
  useEffect(() => {
    if (!pip.supported) return;
    const openPip = () => { void pip.open(buildPipOptions()); };
    try { (navigator.mediaSession as any)?.setActionHandler?.('enterpictureinpicture', openPip); } catch { /* */ }
    const onVis = () => {
      // Öffnen übernimmt Chrome über den MediaSession-Handler (mit Aktivierung).
      // Hier nur schließen, sobald der Tab wieder sichtbar ist.
      if (document.visibilityState === 'visible') pip.close();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      try { (navigator.mediaSession as any)?.setActionHandler?.('enterpictureinpicture', null); } catch { /* */ }
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [pip, buildPipOptions]);

  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const guestVideoRef = useRef<HTMLVideoElement>(null); // Welle 8d / Mesh M1: Experten-Kachel
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const selfVideoRef = useRef<HTMLVideoElement>(null);
  // pre161: Lautsprecher-Klick schaltet den Ton (Audio-Ausgang) stumm.
  const [outputMuted, setOutputMuted] = useState(false);
  // pre162: Lautstärke des Audio-Ausgangs (0..1), Regler im Lautsprecher-Menü.
  const [outputVolume, setOutputVolume] = useState(1);

  // Welle 8c-pre47 (F5-Restore): Nach einem Reload zeigt InCall ein dezentes Overlay,
  // bis die Verbindung wieder steht. Steht sie binnen 20s nicht (Stele-byeGrace abgelaufen
  // = Call vorbei), brechen wir die Wiederaufnahme ab und kehren ins Dashboard zurück.
  const [restoring, setRestoring] = useState<boolean>(() => Boolean((window as any).__f5Restoring));
  useEffect(() => {
    if (!restoring) return;
    if (rtc.status === 'connected') {
      (window as any).__f5Restoring = false;
      setRestoring(false);
      console.log('[f5] Call nach Reload wiederhergestellt');
      return;
    }
    const t = setTimeout(() => {
      if (rtc.status !== 'connected') {
        console.warn('[f5] Wiederaufnahme nach 20s ohne Verbindung → Call vermutlich beendet');
        (window as any).__f5Restoring = false;
        clearCallSession();
        setRestoring(false);
        setView('dashboard');
      }
    }, 20000);
    return () => clearTimeout(t);
  }, [restoring, rtc.status, setView]);

  // Welle 8c-pre49: Dokumentenbeleuchtung (Relais Kanal 1 auf der Stele). Der Button
  // schaltet optimistisch und korrigiert sich anhand der light.state-Bestätigung der Stele.
  const [lightOn, setLightOn] = useState(false);
  const [lightPending, setLightPending] = useState(false);
  useEffect(() => {
    const onLight = (e: Event) => {
      const d = (e as CustomEvent).detail as { on: boolean; ok: boolean; error?: string };
      setLightPending(false);
      if (d.ok) {
        setLightOn(d.on);
      } else {
        setLightOn(!d.on); // Schalten fehlgeschlagen → vorherigen Zustand wiederherstellen
        console.warn('[incall] Licht schalten fehlgeschlagen:', d.error);
      }
    };
    window.addEventListener('bamoa:light-state', onLight);
    return () => window.removeEventListener('bamoa:light-state', onLight);
  }, []);

  function toggleLight() {
    const next = !lightOn;
    setLightOn(next);        // optimistisch
    setLightPending(true);
    const sent = rtc.sendCamCommand({ type: 'light.set', on: next });
    if (!sent) { setLightOn(!next); setLightPending(false); } // Steuerkanal nicht offen
  }

  // Welle 8c-pre32: Der WebRTC-Hook schreibt die Live-Statistik beider Seiten jetzt
  // selbst in den Store (Verbindungsqualitäts-Kachel) und baut beim Gesprächsende die
  // Qualitätszusammenfassung. InCall kümmert sich nur noch um den Lebenszyklus:
  //  - beim Mount die Zusammenfassung des VORHERIGEN Calls verwerfen
  //  - beim Unmount die Live-Stats zurücksetzen (lastCallSummary bleibt für die Wrap-Ansicht)
  const setCallSummary = useUIStore((s) => s.setCallSummary);
  const resetCallStats = useUIStore((s) => s.resetCallStats);
  useEffect(() => {
    setCallSummary(null);
    return () => { resetCallStats(); };
  }, [setCallSummary, resetCallStats]);

  // Welle 8c: Bildschirmfreigabe ein/aus. rtc.screenSharing ist die Wahrheit (auch
  // wenn der Nutzer über die Browser-Leiste beendet), Button + S-Shortcut nutzen das.
  const handleToggleShare = useCallback(() => {
    if (rtc.screenSharing) void rtc.stopScreenShare();
    else void rtc.startScreenShare();
  }, [rtc.screenSharing, rtc.startScreenShare, rtc.stopScreenShare]);

  useEffect(() => {
    const h = () => handleToggleShare();
    window.addEventListener('bamoa:toggle-share', h);
    return () => window.removeEventListener('bamoa:toggle-share', h);
  }, [handleToggleShare]);

  // Welle 7e: echte Aufzeichnung (Topic-gated, einwilligungspflichtig).
  // Status wird an die Stele gemeldet, damit der Gast den Indikator sieht.
  const recorder = useCallRecording((active, topic) => rtc.sendRecordingState(active, topic));
  recStopRef.current = recorder.stop;
  const [consentOpen, setConsentOpen] = useState(false);
  const [consentTopic, setConsentTopic] = useState<string>('geldanlage');
  const [consentPhase, setConsentPhase] = useState<'select' | 'waiting' | 'declined'>('select');
  const [recPolicy, setRecPolicy] = useState<RecordingPolicy>(DEFAULT_POLICY);
  useEffect(() => {
    void fetchRecordingPolicy().then((pol) => {
      setRecPolicy(pol);
      const first = pol.topics.find((t) => t.enabled);
      if (first) setConsentTopic((cur) => (pol.topics.some((t) => t.id === cur && t.enabled) ? cur : first.id));
    }).catch(() => { /* permissiver Default */ });
  }, []);

  // Welle 12 (pre135): A/V-Status an die Companion-Sitzung spiegeln — inkl. Recording-Status,
  // damit der Companion REC / „warte auf Einwilligung" anzeigen kann. NACH der recorder-/
  // consent-Deklaration platziert (sonst TDZ im Dependency-Array).
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('bamoa:remote-state', { detail: { inCall: true, micMuted, camOn, guestConnected: !!rtc.remoteGuestStream, recording: recorder.recording, recPending: consentOpen && consentPhase === 'waiting', recTopic: consentTopic } }));
  }, [micMuted, camOn, rtc.remoteGuestStream, recorder.recording, consentOpen, consentPhase, consentTopic]);

  function onRecordButton() {
    if (recorder.recording) { void recorder.stop(); return; }
    setConsentPhase('select');
    setConsentOpen(true);
  }

  // Welle 12 (pre135): Fern-Start aus dem Companion — Thema ist dort bereits gewählt, daher
  // direkt in die Einwilligungsphase (Gast bestätigt auf der Stele). Läuft schon eine
  // Aufzeichnung, ist der Befehl ein No-op.
  function triggerRecordingStart(topic?: string) {
    if (recorder.recording) return;
    if (topic) setConsentTopic(topic);
    setConsentOpen(true);
    setConsentPhase('waiting');
    void requestConsentFromStele(topic);
  }

  // Berater fordert die aktive Zustimmung auf der Stele an (keine mündliche Zustimmung).
  async function requestConsentFromStele(topicArg?: string) {
    const topic = topicArg ?? consentTopic;
    const callId = (useUIStore.getState() as any).activeCallId ?? (window as any).__activeCallId ?? null;
    if (!callId) {
      console.warn('[rec] kein activeCallId — Aufzeichnung nicht möglich');
      setConsentOpen(false); setConsentPhase('select');
      return;
    }
    if (!topicRequiresRecording(topic)) { setConsentOpen(false); return; }
    if (!policyAllowsTopic(recPolicy, topic)) {
      console.warn('[rec] Policy verbietet Aufzeichnung für dieses Thema');
      setConsentOpen(false); setConsentPhase('select');
      return;
    }
    const topicDef = getTopic(recPolicy, topic);
    const mode: 'hinweis' | 'einwilligung' = topicDef?.consentMode ?? 'einwilligung';
    setConsentPhase('waiting');
    const consent = await rtc.requestRecordingConsent(topic, mode);
    if (consent.cancelled) return;                       // vom Berater abgebrochen — Modal ist bereits zu
    if (!consent.accepted) { setConsentPhase('declined'); return; } // Gast hat aktiv abgelehnt (oder Timeout)
    setConsentOpen(false); setConsentPhase('select');
    // Getrennte Audio-Spuren je Partei (MiFID-Kern); Besucher-Audio bevorzugt aus dem separaten Track.
    await recorder.start(callId, topic, {
      berater: rtc.localStream,
      besucher: rtc.remoteStreamAudio ?? rtc.remoteStreamMain,
    }, { consentMode: mode, consentAt: consent.at }, {
      delivery: deliveryAdapterFor(recPolicy, topic),
      recipientKey: recPolicy.recipientPublicKey,
    });
  }

  function cancelConsentWaiting() {
    rtc.cancelRecordingConsent();
    setConsentOpen(false); setConsentPhase('select');
  }
  function closeConsentModal() { setConsentOpen(false); setConsentPhase('select'); }

  useEffect(() => {
    if (remoteVideoRef.current && rtc.remoteStreamMain) {
      remoteVideoRef.current.srcObject = rtc.remoteStreamMain;
    }
  }, [rtc.remoteStreamMain]);
  // Welle 8c-pre9: Preview-srcObject NUR bei tatsächlichem Stream-Wechsel zuweisen.
  // Stabiler Ref-Callback (Identität ändert sich nur, wenn sich der Stream ändert),
  // mit Guard gegen redundante Zuweisung. Verhindert, dass der 1-Sekunden-Timer-Render
  // srcObject ständig neu setzt — genau das erzeugte das ~1 Schwarzbild/Sekunde.
  const attachPreviewVideo = useCallback((el: HTMLVideoElement | null) => {
    if (el && rtc.remoteStreamPreview && el.srcObject !== rtc.remoteStreamPreview) {
      el.srcObject = rtc.remoteStreamPreview;
    }
  }, [rtc.remoteStreamPreview]);

  // Welle 8d / Mesh M1: Experten-Stream an die Kachel binden.
  useEffect(() => {
    if (guestVideoRef.current && rtc.remoteGuestStream) {
      guestVideoRef.current.srcObject = rtc.remoteGuestStream;
      guestVideoRef.current.play().catch(() => { /* Autoplay ggf. blockiert */ });
    }
  }, [rtc.remoteGuestStream]);
  // Welle 4f: separater Audio-Track läuft in eigenem <audio>-Element
  useEffect(() => {
    if (remoteAudioRef.current && rtc.remoteStreamAudio) {
      remoteAudioRef.current.srcObject = rtc.remoteStreamAudio;
      // Welle 8c-pre: gemerkten Lautsprecher anwenden (Chromium)
      const spk = getPreferredDevice('audiooutput');
      const el = remoteAudioRef.current as any;
      if (spk && typeof el.setSinkId === 'function') {
        el.setSinkId(spk).catch((e: any) => {
          if (e?.name !== 'AbortError') console.warn('[incall] setSinkId failed', e);
        });
      }
    }
  }, [rtc.remoteStreamAudio]);
  // pre161: Ausgang stummschalten — wirkt auf das dedizierte Audio-Element (Call-Ton).
  useEffect(() => {
    if (remoteAudioRef.current) remoteAudioRef.current.muted = outputMuted;
  }, [outputMuted, rtc.remoteStreamAudio]);
  // pre162: Lautstärke auf das Audio-Element anwenden.
  useEffect(() => {
    if (remoteAudioRef.current) remoteAudioRef.current.volume = outputVolume;
  }, [outputVolume, rtc.remoteStreamAudio]);
  // Welle 8c-pre: Lautsprecher (Audio-Ausgang) wählen — wirkt auf Audio- und Video-Element
  const setSpeaker = async (deviceId: string) => {
    setPreferredDevice('audiooutput', deviceId);
    for (const el of [remoteAudioRef.current, remoteVideoRef.current] as any[]) {
      if (el && typeof el.setSinkId === 'function') {
        try { await el.setSinkId(deviceId); } catch (e: any) {
          if (e?.name !== 'AbortError') console.warn('[incall] setSinkId failed', e);
        }
      }
    }
  };
  useEffect(() => {
    if (selfVideoRef.current && rtc.localPortraitStream) {
      selfVideoRef.current.srcObject = rtc.localPortraitStream;
    }
  }, [rtc.localPortraitStream]);

  // Welle 6: NFC-Events vom useWebRtc-Hook (dispatched per Window-Event) in den Store schreiben
  const setNfcData = useUIStore((s) => s.setNfcData);
  const showHeaderChip = useUIStore((s) => s.showHeaderChip);
  const addSessionDoc = useUIStore((s) => s.addSessionDoc);
  const clearSessionDocs = useUIStore((s) => s.clearSessionDocs);
  useEffect(() => {
    function onNfc(e: Event) {
      const detail = (e as CustomEvent).detail as NfcReadPayload | undefined;
      if (!detail) return;
      setNfcData(detail);
      showHeaderChip('nfc', 8000);
      // Welle 8c-pre38: Protokoll-Eintrag mit allen gelesenen Feldern in die Session-Ablage
      if (detail.success) {
        const fields: Record<string, string> = {};
        if (detail.bancName) fields['Bank'] = detail.bancName;
        if (detail.blz) fields['BLZ'] = detail.blz;
        if (detail.bic) fields['BIC'] = detail.bic;
        if (detail.accountNumber) fields['Konto'] = detail.accountNumber;
        if (detail.pan) fields['PAN'] = detail.pan;
        if (detail.chipCardNumber) fields['Karten-Nr.'] = detail.chipCardNumber;
        if (detail.expirationDate) fields['Gültig bis'] = detail.expirationDate;
        if (detail.bancLocation) fields['Ort'] = detail.bancLocation;
        const t = new Date();
        addSessionDoc({
          kind: 'nfc',
          name: `EC-Karte${detail.bancName ? ' · ' + detail.bancName : ''}`,
          status: `Gelesen · ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`,
          data: fields,
        });
        // pre87c/pre89b: EC-Karten-Protokoll lokal (im Browser) für die Nachbearbeitung sichern — als PDF.
        const cid = getActiveCallId();
        if (cid) {
          const pdf = buildEcProtocolPdf(fields, { callId: cid, at: t.getTime() });
          void captureBlob(cid, pdf, {
            kind: 'nfc',
            mime: 'application/pdf',
            name: `EC-Karte ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}.pdf`,
            docId: `nfc-${cid}-${t.getTime()}`,
          });
        }
      }
    }
    window.addEventListener('bamoa:nfc', onNfc);
    return () => window.removeEventListener('bamoa:nfc', onNfc);
  }, [setNfcData, showHeaderChip, addSessionDoc]);

  // Mic-Toggle und Cam-Toggle ans WebRTC weiterleiten
  useEffect(() => {
    if (rtc.localStreamMain) {
      rtc.localStreamMain.getAudioTracks().forEach((t) => (t.enabled = !micMuted));
    }
  }, [micMuted, rtc.localStreamMain]);
  useEffect(() => {
    if (rtc.localStreamMain) {
      rtc.localStreamMain.getVideoTracks().forEach((t) => (t.enabled = camOn));
    }
  }, [camOn, rtc.localStreamMain]);

  const [dropping, setDropping] = useState(false);
  // Welle 4e: Swap-Animation endet erst wenn der neue Stream wirklich im
  // <video>-Element gerendert wird (playing-Event) UND mindestens 400ms
  // Animation-Zeit vergangen sind. Vorher Variante 4d nutzte fixen 450ms-
  // Timer, was bei sparse-Profil (mit applyConstraints) zu kurz war.
  const [swapping, setSwapping] = useState(false);
  const swapStartRef = useRef<number>(0);
  const swapStreamIdAtStartRef = useRef<string | null>(null);
  const SWAP_MIN_MS = 400;
  const SWAP_MAX_MS = 5000; // Safety: spätestens nach 5s aus

  function triggerSwap() {
    if (swapping) return;
    swapStartRef.current = performance.now();
    swapStreamIdAtStartRef.current = rtc.remoteStreamMain?.id ?? null;
    setSwapping(true);
    swapCameras();
    rtc.requestSwap();
    // Safety-Timer: falls der Stream-Wechsel hängt, Animation nicht endlos zeigen
    setTimeout(() => setSwapping(false), SWAP_MAX_MS);
  }

  // Wenn das remoteStreamMain-Objekt wechselt UND ein Swap läuft → Animation
  // beenden, sobald das <video>-Element den neuen Stream wirklich abspielt.
  useEffect(() => {
    if (!swapping) return;
    if (!rtc.remoteStreamMain) return;
    if (rtc.remoteStreamMain.id === swapStreamIdAtStartRef.current) return; // noch alter Stream
    const v = remoteVideoRef.current;
    if (!v) return;
    function endSwap() {
      const elapsed = performance.now() - swapStartRef.current;
      const wait = Math.max(0, SWAP_MIN_MS - elapsed);
      setTimeout(() => setSwapping(false), wait);
    }
    // Wenn das Element schon spielt (was passieren kann wenn Stream-Wechsel sehr schnell war),
    // sofort beenden. Sonst auf 'playing'-Event warten.
    if (!v.paused && v.readyState >= 2 && v.currentTime > 0) {
      endSwap();
      return;
    }
    v.addEventListener('playing', endSwap, { once: true });
    v.addEventListener('loadeddata', endSwap, { once: true });
    return () => {
      v.removeEventListener('playing', endSwap);
      v.removeEventListener('loadeddata', endSwap);
    };
  }, [rtc.remoteStreamMain, swapping]);
  const stageRef = useRef<HTMLDivElement>(null);

  async function endCall() {
    // pre98: laufende Aufzeichnung zuerst finalisieren (Paket bauen + ablegen), dann auflegen.
    try { await recorder.stop(); } catch { /* */ }
    try { pip.close(); } catch { /* */ }
    playSound('hangup', audioPrefs);
    rtc.hangup();
    // Welle 8c-pre47: bewusstes Auflegen → F5-Kontext löschen (keine Wiederaufnahme)
    clearCallSession();
    (window as any).__f5Restoring = false;
    setView('wrap');
  }

  // Welle 5: Snapshot-Viewer State
  const [snapshotViewerUrl, setSnapshotViewerUrl] = useState<string | null>(null);
  const [snapshotViewerMime, setSnapshotViewerMime] = useState<string>(''); // pre216
  const [snapshotViewerName, setSnapshotViewerName] = useState<string>(''); // pre216
  const [placement, setPlacement] = useState<{ file: File; bytes: ArrayBuffer; filename: string } | null>(null); // pre224
  const openPlacement = useCallback((file: File) => { void (async () => { try { const bytes = await file.arrayBuffer(); setPlacement({ file, bytes, filename: file.name }); } catch (e) { console.warn('[place] Datei nicht lesbar', e); } })(); }, []);
  // pre218: Client-seitiges PAdES-Siegel (Weg B)
  const [sealLabel, setSealLabel] = useState<string | null>(null); // pre228: aus IndexedDB geladen
  const [sealStatus, setSealStatus] = useState<'idle' | 'sealing' | 'sealed' | 'error'>('idle');
  const [sealError, setSealError] = useState<string | null>(null); // pre231: sichtbarer Siegel-Fehler
  const [tenantSealAvailable, setTenantSealAvailable] = useState<string | null>(null); // pre235: zentrales Zertifikat vorhanden?
  const otpChallengeBySigRef = useRef<Map<string, string>>(new Map()); // pre239: sigId → challengeId
  const [otpResults, setOtpResults] = useState<Record<string, boolean>>({}); // pre239: sigId → verifiziert?
  const [awaitingIdCapture, setAwaitingIdCapture] = useState<{ sigId: string; base: string } | null>(null); // pre239
  const [idImages, setIdImages] = useState<Record<string, { url: string; mime: string }>>({}); // pre240: Ausweisbild je Signatur
  const [reviewBeforeSeal, setReviewBeforeSeal] = useState(false); // pre247: Mandanten-Einstellung
  useEffect(() => { void apiFetch('/api/sign-settings/status').then(async (r) => { if (r.ok) { const j = await r.json(); setReviewBeforeSeal(!!j.reviewBeforeSeal); } }).catch(() => {}); }, []);
  const [pendingReview, setPendingReview] = useState<{ sigId: string; filename: string; buf: ArrayBuffer } | null>(null); // pre247
  const sealedUrlsRef = useRef<Set<string>>(new Set());
  // pre228: dauerhaft gespeichertes (nicht-exportierbares) Siegel-Zertifikat beim Mount laden
  useEffect(() => { void import('@/lib/sealCryptoKey').then((m) => m.getStoredSealLabel()).then(setSealLabel).catch(() => {}); }, []);
  // pre236: Settings melden Änderungen am lokalen Schlüssel → Pille aktualisieren.
  useEffect(() => {
    const onChanged = () => { void import('@/lib/sealCryptoKey').then((m) => m.getStoredSealLabel()).then(setSealLabel).catch(() => {}); };
    window.addEventListener('bamoa:seal-changed', onChanged);
    return () => window.removeEventListener('bamoa:seal-changed', onChanged);
  }, []);
  // pre235: prüfen, ob der Mandant ein zentrales Siegel-Zertifikat hinterlegt hat (nur Anzeige).
  useEffect(() => {
    // pre254: leichter Status-Endpunkt — lädt NICHT das Chiffrat (das wäre pro Call ein
    // auditierter Schlüsselmaterial-Abruf); nur Label für die Status-Pille.
    void apiFetch('/api/seal-cert/status').then(async (r) => { if (r.ok) { const j = await r.json(); if (j) setTenantSealAvailable(j.label ?? 'Siegel-Zertifikat'); } }).catch(() => {});
  }, []);
  // pre233 (Stage B): Mandanten-Zertifikat vom Backend holen (Chiffrat) und client-seitig
  // entschlüsseln. Server sieht nur das Chiffrat. Rückgabe: true wenn importiert.
  const loadTenantSealCert = useCallback(async (): Promise<boolean> => {
    try {
      const res = await apiFetch('/api/seal-cert');
      if (res.status === 404) return false; // kein zentrales Zertifikat hinterlegt
      if (!res.ok) { console.warn('[seal] Mandanten-Zertifikat: HTTP', res.status); return false; }
      const enc = await res.json();
      const passphrase = window.prompt(`Zentrales Siegel-Zertifikat gefunden${enc.label ? ` („${enc.label}")` : ''}.\nMandanten-Passphrase eingeben:`);
      if (passphrase == null) return true; // abgebrochen — nicht auf Datei-Dialog zurückfallen
      const { decryptP12 } = await import('@/lib/sealCertCrypto');
      const p12 = await decryptP12(enc, passphrase);
      const { importP12ToStore } = await import('@/lib/sealCryptoKey');
      // P12-Innenverschlüsselung nutzt i. d. R. dieselbe Passphrase wie der Export; wir
      // probieren erst die Mandanten-Passphrase, dann leer (ungeschützte Test-P12s).
      let label: string;
      try { label = await importP12ToStore(p12, passphrase, enc.uploadedAt); }
      catch { label = await importP12ToStore(p12, '', enc.uploadedAt); }
      setSealLabel(label);
      return true;
    } catch (e) {
      console.error('[seal] Mandanten-Zertifikat laden fehlgeschlagen', e);
      window.alert('Entschlüsselung fehlgeschlagen — Mandanten-Passphrase prüfen.');
      return true; // Fehler gemeldet; nicht zusätzlich Datei-Dialog öffnen
    }
  }, []);

  // pre218: Trifft ein PDF ein und ist ein Siegel-Zertifikat geladen, wird es
  // AUTOMATISCH client-seitig versiegelt und die gesiegelte Version angezeigt.
  useEffect(() => {
    if (!snapshotViewerUrl || !snapshotViewerMime.includes('pdf')) return;
    if (!sealLabel) { setSealStatus('idle'); return; } // kein Schlüssel geladen → nicht siegeln
    if (sealedUrlsRef.current.has(snapshotViewerUrl)) return; // bereits gesiegelt / ist schon die gesiegelte Version
    const src = snapshotViewerUrl;
    const baseName = (snapshotViewerName || 'dokument').replace(/\.pdf$/i, '');
    sealedUrlsRef.current.add(src);
    setSealStatus('sealing');
    void (async () => {
      try {
        const buf = await (await fetch(src)).arrayBuffer();
        const { sealPdfWithStoredKey } = await import('@/lib/padesSealWebCrypto');
        const sealed = await sealPdfWithStoredKey(buf, { name: sealLabel });
        const url = URL.createObjectURL(new Blob([sealed as BlobPart], { type: 'application/pdf' }));
        sealedUrlsRef.current.add(url);
        setSnapshotViewerUrl(url);
        setSnapshotViewerName(baseName + '-gesiegelt.pdf');
        setSealStatus('sealed');
      } catch (e) { console.error('[seal] Siegeln fehlgeschlagen', e); setSealStatus('error'); setSealError(String((e as any)?.message || e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotViewerUrl, snapshotViewerMime, snapshotViewerName, sealLabel]);
  // Welle 8c-pre38: Dokumenten-Signierung (PDF über lokale SignController-API der Stele)
  const sigFileInputRef = useRef<HTMLInputElement | null>(null);
  const [sigViewerDismissed, setSigViewerDismissed] = useState<string | null>(null);
  const [sigToastDismissed, setSigToastDismissed] = useState<string | null>(null);

  // pre89d: Ablage erst nach Sichtprüfung im Viewer (Scan/Signatur) + Erfolgsmeldung mit Link.
  type SaveUi = { status: 'idle' | 'saving' | 'saved' | 'err'; folder?: string; err?: string };
  const [scanSave, setScanSave] = useState<SaveUi>({ status: 'idle' });
  const [sigSave, setSigSave] = useState<SaveUi>({ status: 'idle' });
  // Neuer Scan / neue Signatur → Speicherstatus zurücksetzen
  useEffect(() => { setScanSave({ status: 'idle' }); }, [snapshotViewerUrl]);
  useEffect(() => { setSigSave({ status: 'idle' }); }, [rtc.lastSignature?.sigId]);

  async function saveDocFromUrl(
    opts: { url: string; kind: TargetKind; mime: string; baseName: string; forceDialog?: boolean },
    setState: (s: SaveUi) => void,
  ) {
    setState({ status: 'saving' });
    try {
      const cid = getActiveCallId() ?? 'unbekannt';
      const blob = await fetch(opts.url).then((r) => r.blob());
      const src = { callId: cid, at: Date.now(), mime: opts.mime || blob.type, name: opts.baseName, blob };
      const { blob: stamped, name } = await stampForExport(src);
      const hasDir = !opts.forceDialog && !!(await getDirName(opts.kind));
      if (hasDir) {
        const res = await saveToDir(opts.kind, name, stamped, { interactive: true });
        if (res.ok) { setState({ status: 'saved', folder: (await getDirName(opts.kind)) ?? undefined }); return; }
        if (res.reason === 'denied') { setState({ status: 'err', err: 'Kein Schreibzugriff auf den Ordner — bitte „Speichern unter …" nutzen.' }); return; }
        // 'error' → auf Dialog ausweichen
      }
      const out = await saveWithDialog(name, stamped, opts.kind);
      setState(out === 'cancelled' ? { status: 'idle' } : { status: 'saved' });
    } catch {
      setState({ status: 'err', err: 'Speichern fehlgeschlagen.' });
    }
  }
  const sigFiledRef = useRef<Set<string>>(new Set());   // bereits abgelegte signierte PDFs
  const scanFiledRef = useRef<Set<string>>(new Set());  // bereits abgelegte Scans
  // Welle 8c-pre43: Inspection-Controls — Zoom-Slider + Minimap-Drag
  const [sliderZoom, setSliderZoom] = useState(1);
  const zoomDragRef = useRef(false);
  const lastZoomSentRef = useRef(0);
  const lastPanSentRef = useRef(0);
  const minimapRef = useRef<HTMLDivElement | null>(null);
  const sigPrevDocFocusedRef = useRef<boolean | null>(null); // Ansicht vor dem Signieren
  // Session-Ablage je Call frisch starten (Nachbearbeitung des Vor-Calls bleibt bis dahin erhalten)
  useEffect(() => { clearSessionDocs(); }, [clearSessionDocs]);
  // Welle 6a: Self-Preview Modal State
  const [selfPreviewOpen, setSelfPreviewOpen] = useState(false);
  useEffect(() => {
    if (!rtc.lastScan) return;
    if (rtc.lastScan.error) {
      console.warn('[incall] scan error:', rtc.lastScan.error);
      return;
    }
    if (rtc.lastScan.url) {
      setSnapshotViewerUrl(rtc.lastScan.url);
      setSnapshotViewerMime(rtc.lastScan.mime || 'image/jpeg');
      setSnapshotViewerName(rtc.lastScan.name || '');
      // Welle 8c-pre38: Scan zusätzlich in die Session-Ablage legen
      const key = rtc.lastScan.url;
      if (!scanFiledRef.current.has(key)) {
        scanFiledRef.current.add(key);
        const t = new Date();
        addSessionDoc({
          kind: 'scan',
          name: `Snapshot ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`,
          status: `Scan · ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`,
          url: rtc.lastScan.url,
        });
        // pre87c: Scan lokal (im Browser) für die Nachbearbeitung sichern.
        const cid = getActiveCallId();
        const sMime = rtc.lastScan.mime || 'image/jpeg';
        const sExt = sMime.includes('pdf') ? 'pdf' : sMime.includes('png') ? 'png' : sMime.includes('webp') ? 'webp' : sMime.startsWith('image/') ? 'jpg' : 'bin';
        const sName = rtc.lastScan.name || `Scan ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}.${sExt}`;
        if (cid) void captureFromUrl(cid, rtc.lastScan.url, { kind: 'scan', name: sName, mime: sMime, docId: `scan-${cid}-${key}` });
      }
    }
  }, [rtc.lastScan, addSessionDoc]);

  // Welle 8c-pre38: Während des Signierens Doc-Cam groß zeigen, danach Ansicht zurück.
  const sigStatus = rtc.lastSignature?.status;
  useEffect(() => {
    const signing = sigStatus === 'uploading' || sigStatus === 'submitted' || sigStatus === 'signing';
    if (signing && sigPrevDocFocusedRef.current === null) {
      sigPrevDocFocusedRef.current = docFocused;
      if (!docFocused) { swapCameras(); rtc.requestSwap(); }
    } else if (!signing && sigPrevDocFocusedRef.current !== null) {
      const want = sigPrevDocFocusedRef.current;
      sigPrevDocFocusedRef.current = null;
      if (docFocused !== want) { swapCameras(); rtc.requestSwap(); }
    }
    // rtc.requestSwap bewusst nicht in deps – stabile Hook-Funktion
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sigStatus, docFocused, swapCameras]);

  // Welle 8c-pre38: Signiertes PDF in die Session-Ablage legen + server-seitig persistieren.
  useEffect(() => {
    const sig = rtc.lastSignature;
    if (!sig || sig.status !== 'received' || !sig.url) return;
    if (sigFiledRef.current.has(sig.sigId)) return;
    if (sealLabel) return; // pre230: gesiegelte Version wird im Siegel-Effekt abgelegt
    // pre240: auch ohne Siegel erst ablegen, wenn das Ident-Bündel komplett ist,
    // damit Protokollseite + Anlage ins Dokument kommen.
    if (sig.otpCode && otpResults[sig.sigId] === undefined) return;
    if (awaitingIdCapture?.sigId === sig.sigId) return;
    sigFiledRef.current.add(sig.sigId);
    void (async () => {
      let url = sig.url as string;
      if (sig.identProof || sig.otpCode || idImages[sig.sigId]) {
        try {
          const buf = await (await fetch(url)).arrayBuffer();
          const { attachIdentToPdf } = await import('@/lib/signProtocolPage');
          const img = idImages[sig.sigId];
          const idImage = img ? { bytes: await (await fetch(img.url)).arrayBuffer(), mime: img.mime } : undefined;
          const withProto = await attachIdentToPdf(buf, {
            filename: sig.filename ?? 'dokument.pdf',
            identStatus: sig.identStatus,
            identProof: sig.identProof,
            otp: sig.otpCode ? { verified: !!otpResults[sig.sigId] } : undefined,
            idImage,
          });
          url = URL.createObjectURL(new Blob([withProto as BlobPart], { type: 'application/pdf' }));
        } catch (e) { console.warn('[protocol] Anfügen fehlgeschlagen — lege Original ab', e); }
      }
      const t = new Date();
      addSessionDoc({
        kind: 'signed',
        name: sig.filename ?? 'Signiertes Dokument.pdf',
        status: `Signiert · ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`,
        url,
      });
      // pre87c: Signiertes PDF lokal (im Browser) für die Nachbearbeitung sichern.
      const cid = getActiveCallId();
      if (cid) void captureFromUrl(cid, url, { kind: 'signed', name: sig.filename ?? 'Signiertes Dokument.pdf', mime: 'application/pdf', docId: `sig-${cid}-${sig.sigId}` });
      void persistSignedPdf(sig.sigId, sig.filename ?? 'dokument.pdf', url);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtc.lastSignature, addSessionDoc, sealLabel, otpResults, awaitingIdCapture, idImages]);

  // pre239: SMS-Code des Kunden gegen das Backend verifizieren (Berater-Client, P2P-Code).
  const otpVerifiedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const sig = rtc.lastSignature;
    if (!sig || sig.status !== 'received' || !sig.otpCode) return;
    if (otpVerifiedRef.current.has(sig.sigId)) return;
    otpVerifiedRef.current.add(sig.sigId);
    const challengeId = otpChallengeBySigRef.current.get(sig.sigId);
    if (!challengeId) { setOtpResults((r) => ({ ...r, [sig.sigId]: false })); return; }
    void (async () => {
      try {
        const r = await apiFetch('/api/sms-otp/verify', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ challengeId, code: sig.otpCode }) });
        const j = await r.json().catch(() => ({ verified: false }));
        setOtpResults((res) => ({ ...res, [sig.sigId]: !!j.verified }));
      } catch { setOtpResults((res) => ({ ...res, [sig.sigId]: false })); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtc.lastSignature]);

  // pre239: Ausweis-Capture — der nächste eintreffende Kamera-Snapshot wird dem Audit-Paket beigelegt.
  useEffect(() => {
    const scan = rtc.lastScan;
    const wait = awaitingIdCapture;
    if (!wait || !scan?.url || scan.error) return;
    if (!(scan.mime ?? 'image/jpeg').startsWith('image/')) return;
    const ext = (scan.format === 'jpeg' ? 'jpg' : scan.format) || 'jpg';
    const name = `${wait.base}-ausweis.${ext}`;
    addSessionDoc({ kind: 'scan', name, status: 'Ausweis-Erfassung (Ident-Bündel)', url: scan.url });
    const cid = getActiveCallId();
    if (cid) void captureFromUrl(cid, scan.url, { kind: 'scan', name, mime: scan.mime ?? 'image/jpeg', docId: `ident-${cid}-${wait.sigId}-ausweis` });
    setIdImages((m) => ({ ...m, [wait.sigId]: { url: scan.url as string, mime: scan.mime ?? 'image/jpeg' } })); // pre240
    setAwaitingIdCapture(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtc.lastScan, awaitingIdCapture]);

  // pre238: Identifizierungs-Nachweis (WebAuthn) als JSON neben dem Dokument ablegen.
  const identFiledRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const sig = rtc.lastSignature;
    if (!sig || sig.status !== 'received' || (!sig.identProof && !sig.otpCode)) return;
    if (sig.otpCode && otpResults[sig.sigId] === undefined) return; // auf Verifikation warten
    if (identFiledRef.current.has(sig.sigId)) return;
    identFiledRef.current.add(sig.sigId);
    try {
      const json = JSON.stringify({ sigId: sig.sigId, filename: sig.filename, identStatus: sig.identStatus, proof: sig.identProof, otp: sig.otpCode ? { verified: !!otpResults[sig.sigId] } : undefined }, null, 2);
      const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
      const base = (sig.filename || 'dokument').replace(/\.pdf$/i, '');
      addSessionDoc({ kind: 'signed', name: `${base}-identnachweis.json`, status: 'Identitätsnachweis (Passkey)', url });
      const cid = getActiveCallId();
      if (cid) void captureFromUrl(cid, url, { kind: 'signed', name: `${base}-identnachweis.json`, mime: 'application/json', docId: `ident-${cid}-${sig.sigId}` });
    } catch (e) { console.warn('[ident] Nachweis-Ablage fehlgeschlagen', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtc.lastSignature, otpResults]);

  // pre219: Kommt ein signiertes PDF zurück und ist ein Siegel-Zertifikat geladen, wird es
  // automatisch client-seitig versiegelt (PAdES) und im Viewer als signiert+gesiegelt gezeigt.
  const sealedSigRef = useRef<Set<string>>(new Set());
  // pre247: Siegeln + Ablage (aus dem Effekt herausgelöst, damit das Berater-Review
  // denselben Pfad per Klick auslösen kann).
  const sealAndFile = useCallback(async (sigId: string, filename: string, buf: ArrayBuffer) => {
    setSealStatus('sealing');
    const { sealPdfWithStoredKey } = await import('@/lib/padesSealWebCrypto');
    const sealed = await sealPdfWithStoredKey(buf, { name: sealLabel ?? 'Bank' });
    const url = URL.createObjectURL(new Blob([sealed as BlobPart], { type: 'application/pdf' }));
    sealedUrlsRef.current.add(url);
    setSnapshotViewerMime('application/pdf');
    setSnapshotViewerName(filename.replace(/\.pdf$/i, '') + '-signiert-gesiegelt.pdf');
    setSnapshotViewerUrl(url);
    setSealStatus('sealed');
    setSealError(null);
    const st = new Date();
    const sealedName = filename.replace(/\.pdf$/i, '') + '-gesiegelt.pdf';
    addSessionDoc({ kind: 'signed', name: sealedName, status: `Signiert + gesiegelt · ${st.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`, url });
    const cid = getActiveCallId();
    if (cid) void captureFromUrl(cid, url, { kind: 'signed', name: sealedName, mime: 'application/pdf', docId: `sig-${cid}-${sigId}` });
    void persistSignedPdf(sigId, sealedName, url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sealLabel, addSessionDoc]);

  useEffect(() => {
    const sig = rtc.lastSignature;
    if (!sig || sig.status !== 'received' || !sig.url) return;
    if (!sealLabel) return; // kein Schlüssel geladen
    // pre240: erst siegeln, wenn das Ident-Bündel komplett ist — Protokollseite und
    // Anlage müssen VOR dem Siegel ins Dokument (das Siegel deckt sie dann ab).
    if (sig.otpCode && otpResults[sig.sigId] === undefined) return;       // SMS-Verifikation ausstehend
    if (awaitingIdCapture?.sigId === sig.sigId) return;                   // Ausweis-Erfassung ausstehend (× überspringt)
    if (sealedSigRef.current.has(sig.sigId)) return;
    sealedSigRef.current.add(sig.sigId);
    setSealStatus('sealing');
    void (async () => {
      try {
        let buf = await (await fetch(sig.url as string)).arrayBuffer();
        if (sig.identProof || sig.otpCode || idImages[sig.sigId]) {
          const { attachIdentToPdf } = await import('@/lib/signProtocolPage');
          const img = idImages[sig.sigId];
          const idImage = img ? { bytes: await (await fetch(img.url)).arrayBuffer(), mime: img.mime } : undefined;
          buf = (await attachIdentToPdf(buf, {
            filename: sig.filename ?? 'dokument.pdf',
            identStatus: sig.identStatus,
            identProof: sig.identProof,
            otp: sig.otpCode ? { verified: !!otpResults[sig.sigId] } : undefined,
            idImage,
          })).buffer as ArrayBuffer;
        }
        // pre247: optionales Berater-Review — das fertige (aber ungesiegelte) Dokument
        // inkl. Protokollseite wird zur Prüfung gezeigt; erst der Klick siegelt.
        if (reviewBeforeSeal) {
          const purl = URL.createObjectURL(new Blob([buf], { type: 'application/pdf' }));
          sealedUrlsRef.current.add(purl); // nicht erneut auto-siegeln
          setSnapshotViewerMime('application/pdf');
          setSnapshotViewerName((sig.filename || 'dokument').replace(/\.pdf$/i, '') + '-signiert-VORSCHAU.pdf');
          setSnapshotViewerUrl(purl);
          setSealStatus('idle');
          setPendingReview({ sigId: sig.sigId, filename: sig.filename ?? 'dokument.pdf', buf });
          return;
        }
        await sealAndFile(sig.sigId, sig.filename ?? 'dokument.pdf', buf);
      } catch (e) {
        console.error('[seal] Signatur-Siegel fehlgeschlagen', e);
        setSealStatus('error');
        setSealError(String((e as any)?.message || e));
        // Fallback: ungesiegelte Version ablegen, damit nichts verloren geht
        try {
          const st = new Date();
          addSessionDoc({ kind: 'signed', name: sig.filename ?? 'Signiertes Dokument.pdf', status: `Signiert (Siegel fehlgeschlagen) · ${st.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`, url: sig.url as string });
          const cid = getActiveCallId();
          if (cid) void captureFromUrl(cid, sig.url as string, { kind: 'signed', name: sig.filename ?? 'Signiertes Dokument.pdf', mime: 'application/pdf', docId: `sig-${cid}-${sig.sigId}` });
          void persistSignedPdf(sig.sigId, sig.filename ?? 'dokument.pdf', sig.url as string);
        } catch { /* */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rtc.lastSignature, sealLabel, otpResults, awaitingIdCapture, idImages, reviewBeforeSeal, sealAndFile]);

  function triggerSnapshot() {
    playSound('snap', audioPrefs);
    // Welle 5: echter Scan via DataChannel
    const ok = rtc.sendCamCommand({ type: 'camera.scan', role: 'main', format: 'jpeg' });
    if (!ok) {
      console.warn('[incall] scan-Befehl konnte nicht gesendet werden');
      return;
    }
    // Optische Fly-Animation (bestehend)
    const stage = stageRef.current;
    const docsList = document.getElementById('docs-list');
    if (!stage || !docsList) return;

    const fly = document.createElement('div');
    fly.className = 'snapshot-fly';
    const start = stage.getBoundingClientRect();
    fly.style.top = start.top + start.height / 2 - 60 + 'px';
    fly.style.left = start.left + start.width / 2 - 80 + 'px';
    document.body.appendChild(fly);

    fly.getBoundingClientRect();
    const target = docsList.getBoundingClientRect();
    fly.style.top = target.top + 'px';
    fly.style.left = target.left + 8 + 'px';
    fly.style.width = '40px';
    fly.style.height = '30px';
    fly.style.opacity = '0';
    setTimeout(() => fly.remove(), 700);
  }

  // pre159: Snapshot über die Befehls-Palette auslösbar machen (window-Event).
  // Ref-Pattern gegen stale closure (triggerSnapshot wird je Render neu erzeugt).
  const snapRef = useRef(triggerSnapshot);
  snapRef.current = triggerSnapshot;
  useEffect(() => {
    const onSnap = () => snapRef.current();
    window.addEventListener('bamoa:snapshot', onSnap);
    return () => window.removeEventListener('bamoa:snapshot', onSnap);
  }, []);

  // Welle 5: Zoom-/ROI-Steuerung über DataChannel
  function camZoom(factor: number) {
    rtc.sendCamCommand({ type: 'camera.roi.zoom', role: 'main', factor });
  }
  function camPan(dx: number, dy: number) {
    rtc.sendCamCommand({ type: 'camera.roi.move', role: 'main', dx, dy });
  }
  function camReset() {
    rtc.sendCamCommand({ type: 'camera.roi.reset', role: 'main' });
  }
  // Welle 8c-pre43: Zoom per Schieberegler. Sendet gedrosselt während des Ziehens.
  function setZoomFromSlider(v: number, final: boolean) {
    setSliderZoom(v);
    const now = Date.now();
    if (final || now - lastZoomSentRef.current > 80) {
      lastZoomSentRef.current = now;
      rtc.sendCamCommand({ type: 'camera.roi.zoom', role: 'main', factor: v });
    }
  }
  // Welle 8c-pre43: Zoombereich per Maus auf der Minimap verschieben (ROI zentrieren).
  function panRoiToPoint(clientX: number, clientY: number) {
    const el = minimapRef.current;
    const roi = rtc.roiState.main?.roi;
    if (!el || !roi) return;
    const r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return;
    const relX = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
    const relY = Math.min(1, Math.max(0, (clientY - r.top) / r.height));
    const w = roi.w; const h = roi.h;
    const x = Math.min(1 - w, Math.max(0, relX - w / 2));
    const y = Math.min(1 - h, Math.max(0, relY - h / 2));
    const now = Date.now();
    if (now - lastPanSentRef.current < 40) return;
    lastPanSentRef.current = now;
    rtc.sendCamCommand({ type: 'camera.roi.set', role: 'main', roi: { x, y, w, h } });
  }

  // Welle 8c-pre38: PDF vom Rechner wählen und zum Signieren an die Stele schicken.
  function triggerSignature() {
    setSigViewerDismissed(null);
    setSigToastDismissed(null);
    sigFileInputRef.current?.click();
  }
  function onSigFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // erneute Auswahl derselben Datei erlauben
    if (!file) return;
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) { console.warn('[incall] nur PDF-Dateien können signiert werden'); return; }
    openPlacement(file); // pre225: gleicher Weg wie Drag&Drop → Platzierungs-Modal
  }

  // Signiertes PDF je Call persistieren (Nachbearbeitung/Audit).
  async function persistSignedPdf(sigId: string, filename: string, url: string) {
    const callId = (useUIStore.getState() as any).activeCallId ?? (window as any).__activeCallId ?? null;
    if (!callId) { console.warn('[incall] kein aktiver Call für Signatur-Persistenz'); return; }
    try {
      const blob = await (await fetch(url)).blob();
      const dataUrl: string = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
      const res = await apiFetch(`/api/calls/${callId}/signature`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sigId, filename, dataUrl }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch (e) {
      console.warn('[incall] signiertes PDF persistieren fehlgeschlagen', e);
    }
  }

  const mainRoi = rtc.roiState.main;
  const mainCap = rtc.capabilities.main;
  const currentZoom = mainRoi?.effectiveZoom ?? 1;
  const maxZoom = mainCap?.maxDetailZoom ?? 1;
  const zoomMax = Math.max(2, Math.round(maxZoom * 10) / 10); // Slider-Obergrenze
  const isZoomed = currentZoom > 1.01;
  // Slider folgt dem tatsächlichen Zoom, außer während des Ziehens.
  useEffect(() => { if (!zoomDragRef.current) setSliderZoom(currentZoom); }, [currentZoom]);

  // Welle 5: Keyboard-Shortcuts für die (immer sichtbare) Inspection-Steuerung
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Nur wenn nicht im Input-Feld
      const tgt = e.target as HTMLElement;
      if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA')) return;
      const step = 0.05;
      switch (e.key) {
        case '+':
        case '=':
          e.preventDefault();
          camZoom(Math.min(zoomMax, currentZoom * 1.25));
          break;
        case '-':
        case '_':
          e.preventDefault();
          camZoom(Math.max(1, currentZoom / 1.25));
          break;
        case '0':
          e.preventDefault();
          camReset();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          camPan(-step, 0);
          break;
        case 'ArrowRight':
          e.preventDefault();
          camPan(step, 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          camPan(0, -step);
          break;
        case 'ArrowDown':
          e.preventDefault();
          camPan(0, step);
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [currentZoom, zoomMax]);

  const stageCls = [
    'video-stage',
    'inspection-on',
    translationOn ? 'translation-on' : '',
    captionExpanded ? 'caption-expanded' : '',
    dropping ? 'dropping' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="incall-stage view active">
      {/* Welle 8c-pre47 (F5-Restore): Overlay, solange der Call nach einem Reload wieder
          aufgebaut wird. Verschwindet, sobald die Verbindung steht (oder beim Timeout). */}
      {restoring && rtc.status !== 'connected' && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 60,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 14, background: 'rgba(8,10,14,0.82)', backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.18)', borderTopColor: 'rgba(255,255,255,0.85)',
            animation: 'bamoaSpin 0.9s linear infinite',
          }} />
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 500 }}>Verbindung wird wiederhergestellt…</div>
          <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12.5 }}>Das Gespräch wird nach dem Neuladen fortgesetzt</div>
          <style>{`@keyframes bamoaSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      <div
        className={stageCls}
        id="video-stage"
        ref={stageRef}
        onDragOver={(e) => {
          // Nur bei echtem Datei-Drag reagieren (nicht bei internem Drag)
          if (Array.from(e.dataTransfer.types || []).includes('Files')) {
            e.preventDefault();
            setDropping(true);
          }
        }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDropping(false);
          // Welle 8c-pre40: PDF per Drag & Drop signieren (umgeht den langsamen Datei-Dialog)
          const file = e.dataTransfer.files?.[0];
          if (!file) return;
          const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
          if (!isPdf) { console.warn('[incall] nur PDF-Dateien können signiert werden'); return; }
          openPlacement(file);
        }}
      >
        <div className="dropzone">
          <div className="dropzone-label">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>PDF hier ablegen zum Signieren</span>
          </div>
        </div>

        {/* Welle 8c-pre: Geräte-Hinweise — nur für den Berater sichtbar */}
        {(rtc.status === 'error' || (rtc.remoteDeviceStatus && rtc.remoteDeviceStatus.message)) && (
          <div style={{
            position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
            zIndex: 30, display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 480, width: 'calc(100% - 24px)',
          }}>
            {rtc.status === 'error' && (
              <div style={{
                background: 'rgba(176,42,42,0.95)', color: '#fff', padding: '10px 14px',
                borderRadius: 8, fontSize: 13, lineHeight: 1.4, boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              }}>
                <strong>Problem mit deinem Gerät:</strong><br />
                {rtc.error ?? 'Kamera oder Mikrofon konnte nicht geöffnet werden.'}
              </div>
            )}
            {rtc.remoteDeviceStatus?.message && (
              <div style={{
                background: 'rgba(186,117,23,0.95)', color: '#fff', padding: '10px 14px',
                borderRadius: 8, fontSize: 13, lineHeight: 1.4, boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              }}>
                <strong>Hinweis zur Gegenstelle:</strong> {rtc.remoteDeviceStatus.message}
              </div>
            )}
          </div>
        )}

        <div className={`video-main ${swapping ? 'swapping' : ''}`}>
          {rtc.remoteStreamMain && (
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
            />
          )}
          {/* Welle 4f: separates Audio-Element für den dauerhaften Audio-Stream.
              Hidden, autoplay, nicht muted — so wandert Audio nicht beim Kamera-Swap. */}
          <audio
            ref={remoteAudioRef}
            autoPlay
            playsInline
            style={{ display: 'none' }}
          />
          {/* Welle 4f: Quality-Overlay mit echter Live-Auflösung */}
          {rtc.remoteMainMeta && rtc.remoteMainMeta.width && (
            <div className="video-quality-overlay">
              {rtc.remoteMainMeta.width}×{rtc.remoteMainMeta.height}
              {rtc.remoteMainMeta.frameRate ? ` · ${Math.round(rtc.remoteMainMeta.frameRate)}fps` : ''}
            </div>
          )}
          {swapping && (
            <div className="swap-overlay">
              <div className="swap-spinner" />
              <div className="swap-label">Kamera wechselt…</div>
            </div>
          )}
          {!rtc.remoteStreamMain && (
            <div className="customer-avatar">
              {rtc.status === 'waiting-offer' || rtc.status === 'connecting' || rtc.status === 'negotiating'
                ? '⌛'
                : '★'}
            </div>
          )}
        </div>

        <div className="video-topbar">
          <div className="topbar-center">
            <EncryptionBadge
              meter={<AudioLevelMeter stream={rtc.remoteStreamAudio ?? rtc.remoteStreamMain} title="Mikrofon-Pegel der Gegenseite" />}
              info={rtc.connectionInfo}
              label={partnerLabel}
              timer={timer}
              statsAgent={statsAgent}
              statsStele={statsStele}
              rttHistoryAgent={rttHistoryAgent}
              rttHistoryStele={rttHistoryStele}
            />
          </div>
          <div className="topbar-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* pre229: Siegel-Zertifikat jederzeit ladbar/ersetzbar (unabhängig vom PDF-Viewer) */}
            {sealLabel ? (
              <span title="Siegel-Zertifikat aktiv (nicht-exportierbar) — Verwaltung in den Einstellungen (Ablage & Ordner)" style={{ fontSize: 12, color: '#7ee787', background: 'rgba(126,231,135,0.12)', border: '1px solid rgba(126,231,135,0.4)', borderRadius: 12, padding: '3px 10px', whiteSpace: 'nowrap', cursor: 'pointer' }}>
                {'🔒'} Siegel: {sealLabel}
              </span>
            ) : (
              <button onClick={() => { if (tenantSealAvailable) { void loadTenantSealCert(); } else { window.alert('Kein zentrales Siegel-Zertifikat hinterlegt.\nLaden Sie es in den Einstellungen (Ablage & Ordner) oder bitten Sie Ihren Admin, es zentral zu hinterlegen.'); } }} title={tenantSealAvailable ? `Zentrales Siegel-Zertifikat „${tenantSealAvailable}“ aktivieren (Mandanten-Passphrase erforderlich)` : 'Kein Siegel-Zertifikat — Verwaltung in den Einstellungen (Ablage & Ordner)'} style={{ fontSize: 12, color: tenantSealAvailable ? '#ffd479' : '#cfe3e8', background: tenantSealAvailable ? 'rgba(255,212,121,0.12)' : 'rgba(255,255,255,0.08)', border: tenantSealAvailable ? '1px solid rgba(255,212,121,0.45)' : '1px solid rgba(255,255,255,0.22)', borderRadius: 12, padding: '3px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {tenantSealAvailable ? 'Zentrales Siegel aktivieren' : 'Kein Siegel-Zertifikat'}
              </button>
            )}
            {awaitingIdCapture && (
              <span style={{ fontSize: 11.5, color: '#0a2540', background: '#ffd479', border: '1px solid #d9a94f', borderRadius: 10, padding: '3px 9px', whiteSpace: 'nowrap', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                Ausweis erfassen: Kamera auf den Ausweis richten
                <button onClick={() => triggerSnapshot()} style={{ border: 'none', background: '#0a2540', color: '#fff', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 11.5 }}>Snapshot</button>
                <button onClick={() => setAwaitingIdCapture(null)} style={{ border: 'none', background: 'transparent', color: '#0a2540', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>×</button>
              </span>
            )}
            {sealError && (
              <span onClick={() => setSealError(null)} title="Klicken zum Ausblenden" style={{ fontSize: 11.5, color: '#fff', background: '#a11', border: '1px solid #f66', borderRadius: 10, padding: '3px 9px', cursor: 'pointer', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {'⚠'} Siegeln fehlgeschlagen: {sealError}
              </span>
            )}
            {recorder.recording && (
              <div className="rec-indicator show">
                <span className="r-dot"></span>
                <span>REC</span>
                <span className="hash-counter">· {getTopic(recPolicy, consentTopic)?.label ?? consentTopic} · hash-chained</span>
              </div>
            )}
            {showTurnWarn && (
              <div className="header-chip warn" style={{ display: 'inline-flex' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                TURN-Relay aktiv
              </div>
            )}
          </div>
        </div>

        <CaptionOverlay />

        <div
          style={{
            position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)',
            zIndex: 7, width: 120, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center',
          }}
        >
          {/* Steuerkreuz: ↑ oben, ↓ unten, ← links, → rechts, Scan in der Mitte */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '30px 34px 30px',
              gridTemplateRows: '30px 34px 30px',
              gap: 4,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <span />
            <button className="insp-btn" style={{ width: 30, height: 30, borderRadius: 8 }} onClick={() => camPan(0, -0.05)} title="Hoch (↑)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 15 12 9 18 15" /></svg>
            </button>
            <span />
            <button className="insp-btn" style={{ width: 30, height: 30, borderRadius: 8 }} onClick={() => camPan(-0.05, 0)} title="Links (←)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 6 9 12 15 18" /></svg>
            </button>
            <button
              onClick={triggerSnapshot}
              title="Scan / Snapshot"
              style={{
                width: 34, height: 34, borderRadius: 8, cursor: 'pointer',
                background: 'var(--tenant-accent)', color: '#fff', border: 'none',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
              }}
            >
              <svg width="19" height="19" viewBox="0 0 24 24" fill="var(--tenant-accent)" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 13.2 L8.2 6.9 L7.5 5.5 L21 11.7 Z" />
                <rect x="3" y="13.5" width="18" height="5" rx="1" />
                <rect x="7.5" y="12" width="9" height="1.6" rx="0.3" />
                <circle cx="20.2" cy="12.8" r="0.7" fill="currentColor" stroke="none" />
                <circle cx="5.2" cy="17.3" r="0.75" fill="currentColor" stroke="none" />
              </svg>
            </button>
            <button className="insp-btn" style={{ width: 30, height: 30, borderRadius: 8 }} onClick={() => camPan(0.05, 0)} title="Rechts (→)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 6 15 12 9 18" /></svg>
            </button>
            <span />
            <button className="insp-btn" style={{ width: 30, height: 30, borderRadius: 8 }} onClick={() => camPan(0, 0.05)} title="Runter (↓)">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            <span />
          </div>

          {/* Zoom-Schieberegler (Faktor steht daneben) */}
          <div
            className="tx-conf-slider"
            style={{
              width: '100%', gap: 6,
              background: 'rgba(10,10,10,0.5)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
              borderRadius: 999, padding: '3px 9px', boxSizing: 'border-box',
            }}
          >
            <input
              type="range"
              min={1}
              max={zoomMax}
              step={0.1}
              value={Math.min(zoomMax, Math.max(1, sliderZoom))}
              onPointerDown={() => { zoomDragRef.current = true; }}
              onChange={(e) => setZoomFromSlider(parseFloat(e.target.value), false)}
              onPointerUp={(e) => { zoomDragRef.current = false; setZoomFromSlider(parseFloat((e.target as HTMLInputElement).value), true); }}
              title="Zoom"
            />
            <span className="tx-conf-slider-val">{sliderZoom.toFixed(1)}×</span>
          </div>

          {/* Ausschnitt nur bei Zoom — Zoombereich per Maus verschiebbar */}
          {isZoomed && (
            <div
              ref={minimapRef}
              title="Zoombereich verschieben (ziehen)"
              onPointerDown={(e) => { e.currentTarget.setPointerCapture(e.pointerId); panRoiToPoint(e.clientX, e.clientY); }}
              onPointerMove={(e) => { if (e.buttons === 1) panRoiToPoint(e.clientX, e.clientY); }}
              style={{
                position: 'relative', width: '100%', height: 40,
                background: 'rgba(10,10,10,0.45)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                border: '1px solid rgba(255,255,255,0.14)', borderRadius: 6,
                cursor: 'grab', touchAction: 'none',
              }}
            >
              {mainRoi && (
                <div
                  style={{
                    position: 'absolute',
                    left: `${mainRoi.roi.x * 100}%`,
                    top: `${mainRoi.roi.y * 100}%`,
                    width: `${mainRoi.roi.w * 100}%`,
                    height: `${mainRoi.roi.h * 100}%`,
                    border: '2px solid var(--tenant-accent)',
                    background: 'var(--tenant-accent-soft)',
                    borderRadius: 2,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          )}
        </div>

        <div className="cam-thumbs">
          {/* Welle 4c: nur EIN Preview-Thumb. Zeigt immer remoteStreamPreview (das was klein läuft).
              Label sagt welche Kamera gerade klein ist. Klick triggert Swap. */}
          {rtc.hasPreview && rtc.previewEnabled && (
            <div
              className={`cam-thumb ${swapping ? 'swapping' : ''}`}
              onClick={triggerSwap}
              title="Klick: mit dem großen Bild tauschen"
              style={{ cursor: 'pointer', position: 'relative' }}
            >
              <div className="cam-thumb-img doc-pattern" style={{ overflow: 'hidden' }}>
                {rtc.remoteStreamPreview && (
                  <video
                    autoPlay playsInline muted
                    ref={attachPreviewVideo}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', background: '#000' }}
                  />
                )}
              </div>
              {/* Welle 8d/M2: Vorschau ausschalten (Bandbreite sparen) */}
              <button
                onClick={(e) => { e.stopPropagation(); rtc.setPreviewEnabled(false); }}
                title="Vorschau ausschalten — spart Bandbreite fürs Hauptbild"
                style={{
                  position: 'absolute', top: 4, right: 4, zIndex: 2,
                  width: 20, height: 20, borderRadius: '50%', border: 'none', cursor: 'pointer',
                  background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 13, lineHeight: '20px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                }}
              >×</button>
              <div className="cam-thumb-quality">
                {rtc.remotePreviewMeta && rtc.remotePreviewMeta.width
                  ? `${rtc.remotePreviewMeta.width}×${rtc.remotePreviewMeta.height}`
                  : '…'}
              </div>
              <div className="cam-thumb-label">{docFocused ? 'Webcam' : 'Doc-Cam'}</div>
            </div>
          )}
          {/* Welle 8d/M2: Vorschau aus — Umschalten der Hauptkamera bleibt möglich,
              ebenso das Wiedereinschalten der Vorschau. */}
          {rtc.hasPreview && !rtc.previewEnabled && (
            <div className="cam-thumb" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#1a1a1a', color: '#cbd5e1', padding: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.04em', opacity: 0.8 }}>Vorschau aus</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={triggerSwap}
                  title="Hauptkamera wechseln (Webcam ↔ Doc-Cam)"
                  style={{ border: '1px solid #3a3a3a', background: 'transparent', color: '#e5e7eb', borderRadius: 6, padding: '5px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                >Kamera wechseln</button>
                <button
                  onClick={() => rtc.setPreviewEnabled(true)}
                  title="Vorschau wieder einschalten"
                  style={{ border: 'none', background: 'var(--tenant-accent, #7c3aed)', color: '#fff', borderRadius: 6, padding: '5px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
                >Vorschau ein</button>
              </div>
            </div>
          )}
        </div>

        {/* Welle 8d/M2: Bandbreiten-Hinweis. Meldet der Encoder Bandbreiten-Limitierung
            und läuft die Vorschau noch, dem Berater direkt das Abschalten anbieten. */}
        {rtc.connectionInfo?.bandwidthLimited && rtc.previewEnabled && rtc.hasPreview && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, margin: '8px 0',
            padding: '8px 12px', borderRadius: 8,
            background: 'rgba(186,117,23,0.14)', border: '1px solid rgba(186,117,23,0.35)',
            color: '#854F0B', fontSize: 12.5, fontWeight: 500,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
              <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            </svg>
            <span style={{ flex: 1 }}>Die Bandbreite ist knapp. Vorschau abschalten gibt dem Hauptbild mehr Reserve.</span>
            <button
              onClick={() => rtc.setPreviewEnabled(false)}
              style={{ border: 'none', background: '#854F0B', color: '#fff', borderRadius: 6, padding: '6px 11px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >Vorschau aus</button>
          </div>
        )}

        {/* Welle 8c-pre6: Self-View aus den cam-thumbs herausgelöst — klein, oben rechts. */}
        <div
          className="self-view"
          onClick={() => setSelfPreviewOpen(true)}
          style={{ cursor: 'pointer' }}
          title="Klicken: Vollbild – das sieht die Stele"
        >
          {rtc.localPortraitStream && (
            <video
              ref={selfVideoRef}
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
            />
          )}
        </div>

        {/* pre165: NFC-/EC-Kartendaten als eigenes Overlay auf der Bühne (statt im „Kunde"-Block). */}
        <div className="nfc-stage"><NfcBox /></div>

        {/* Welle 8d / Mesh M1: zugeschalteter Experte (Magic-Link-Gast) */}
        {rtc.remoteGuestStream && (
          <div
            style={{
              position: 'absolute', top: 16, left: 16, width: 200, zIndex: 6,
              borderRadius: 12, overflow: 'hidden', border: '2px solid rgba(95,212,196,0.5)',
              background: '#0a2125', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            }}
          >
            <video
              ref={guestVideoRef}
              autoPlay
              playsInline
              style={{ width: '100%', display: 'block', aspectRatio: '4 / 3', objectFit: 'cover', background: '#0a2125' }}
            />
            <div
              style={{
                position: 'absolute', left: 8, bottom: 8, background: 'rgba(0,0,0,0.5)',
                color: '#5fd4c4', fontSize: 12, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
              }}
            >
              {rtc.guestName || 'Experte'} · zugeschaltet
            </div>
            {/* Welle 8d/M2: Experten jederzeit trennen (Call läuft weiter) */}
            <button
              onClick={() => rtc.disconnectGuest()}
              title="Experten trennen"
              style={{
                position: 'absolute', top: 6, right: 6, zIndex: 2,
                display: 'inline-flex', alignItems: 'center', gap: 4,
                border: 'none', cursor: 'pointer', borderRadius: 6,
                background: 'rgba(214,90,90,0.9)', color: '#fff',
                fontSize: 11, fontWeight: 600, padding: '4px 8px',
              }}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
              Trennen
            </button>
          </div>
        )}

        <div className="video-toolbar">
          <div style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6 }}>
            {/* pre244: eigener Mikrofonpegel neben dem Mikro-Symbol */}
            <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: 2 }}><AudioLevelMeter stream={rtc.localStream} title="Ihr Mikrofon-Pegel" /></span>
            <button className={`tb-btn ${micMuted ? 'muted' : ''}`} onClick={toggleMic} title="Mikrofon">
              <svg viewBox="0 0 24 24">
                {micMuted ? (
                  <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
                ) : (
                  <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
                )}
              </svg>
              <span className="tb-btn-tooltip">
                Mikrofon <kbd>M</kbd>
              </span>
            </button>
            <DeviceMenu kind="audioinput" title="Mikrofon wählen" onSelect={(id) => rtc.switchAudioInput(id)} />
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6 }}>
            <button className={`tb-btn ${camOn ? 'active' : ''}`} onClick={toggleCam} title="Kamera">
              <svg viewBox="0 0 24 24">
                <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
              </svg>
              <span className="tb-btn-tooltip">
                Kamera <kbd>C</kbd>
              </span>
            </button>
            <DeviceMenu kind="videoinput" title="Kamera wählen" onSelect={(id) => rtc.switchVideoInput(id)} />
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6 }}>
            <button className={`tb-btn ${outputMuted ? 'muted' : ''}`} title="Lautsprecher" onClick={() => setOutputMuted((m) => !m)}>
              <svg viewBox="0 0 24 24">
                {outputMuted ? (
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.59 3L19 14.41 17.59 16 15 13.41 12.41 16 11 14.59 13.59 12 11 9.41 12.41 8 15 10.59 17.59 8 19 9.41 16.59 12z" />
                ) : (
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                )}
              </svg>
              <span className="tb-btn-tooltip">{outputMuted ? 'Ton aus · klicken zum Aktivieren' : 'Ton an · klicken zum Stummschalten'}</span>
            </button>
            <DeviceMenu kind="audiooutput" title="Lautsprecher wählen" onSelect={(id) => setSpeaker(id)} volume={outputVolume} onVolume={(v) => { setOutputVolume(v); if (v > 0) setOutputMuted(false); }} />
          </div>
          <div style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6 }}>
            <button className={`tb-btn ${bgActive ? 'active' : ''}`} onClick={() => setBgActive(toggleBg().mode !== 'none')} title="Virtueller Hintergrund">
              <svg viewBox="0 0 24 24">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
              <span className="tb-btn-tooltip">Virtueller Hintergrund {bgActive ? '· an' : '· aus'}</span>
            </button>
            <BackgroundMenu onChange={setBgActive} />
          </div>
          <button className={`tb-btn ${rtc.screenSharing ? 'active' : ''}`} onClick={handleToggleShare} title="Bildschirm teilen">
            <svg viewBox="0 0 24 24">
              <path d="M20 18c1.1 0 1.99-.9 1.99-2L22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2H0v2h24v-2h-4zM4 6h16v10H4V6z" />
            </svg>
            <span className="tb-btn-tooltip">
              {rtc.screenSharing ? 'Freigabe beenden' : 'Bildschirm teilen'} <kbd>S</kbd>
            </span>
          </button>

          <div className="tb-sep"></div>

          <div style={{ display: 'inline-flex', alignItems: 'center', marginRight: 6 }}>
            <button className={`tb-btn ${translationOn ? 'active' : ''}`} onClick={toggleTranslation} title="Übersetzung">
              <svg viewBox="0 0 24 24">
                <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z" />
              </svg>
              <span className="tb-btn-tooltip">
                Übersetzung <kbd>T</kbd>
              </span>
            </button>
            <TranslationMenu />
          </div>
          {recPolicy.enabled && enabledTopics(recPolicy).length > 0 && (
          <button className={`tb-btn ${recorder.recording ? 'recording' : ''}`} onClick={onRecordButton} title="Aufzeichnung">
            <svg viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="6" />
            </svg>
            <span className="tb-btn-tooltip">
              Aufzeichnung <kbd>R</kbd>
            </span>
          </button>
          )}

          <div className="tb-sep"></div>

          <button className="tb-btn" onClick={openEscalate} title="Einladung">
            <svg viewBox="0 0 24 24">
              <path d="M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2V7H4v3H1v2h3v3h2v-3h3v-2H6zm9 4c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
            <span className="tb-btn-tooltip">
              Einladung <kbd>⌘E</kbd>
            </span>
          </button>

          <button className="tb-btn" onClick={triggerSignature} title="Dokument signieren">
            <svg viewBox="0 0 24 24">
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
            </svg>
            <span className="tb-btn-tooltip">Dokument signieren</span>
          </button>
          <input
            ref={sigFileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            style={{ display: 'none' }}
            onChange={onSigFileChosen}
          />

          <button className="tb-btn" onClick={triggerSwap} title="Kamera wechseln">
            <svg viewBox="0 0 24 24"><path d="M20 4h-3.17L15 2H9L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 11.5V13H9v2l-3-3 3-3v2h6v-2l3 3-3 3z" /></svg>
            <span className="tb-btn-tooltip">Kamera wechseln</span>
          </button>
          <button
            className={`tb-btn ${lightOn ? 'active' : ''}`}
            onClick={toggleLight}
            disabled={lightPending}
            title={lightOn ? 'Dokumentenlicht aus' : 'Dokumentenlicht an'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 18h6" />
              <path d="M10 22h4" />
              <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1v.2h6v-.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z" />
            </svg>
            <span className="tb-btn-tooltip">{lightOn ? 'Dokumentenlicht aus' : 'Dokumentenlicht an'}</span>
          </button>

          {pip.supported && (
            <button className={`tb-btn ${pip.active ? 'active' : ''}`} onClick={togglePip} title="Im Vordergrund halten (Picture-in-Picture)">
              <svg viewBox="0 0 24 24">
                <path d="M19 7h-8v6h8V7zm2-4H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.11-.9-2-2-2zm0 16.01H3V4.98h18v14.03z" />
              </svg>
              <span className="tb-btn-tooltip">
                Mini-Fenster
              </span>
            </button>
          )}
          <button className="tb-btn" onClick={openTransfer} title="Weiterleitung">
            <svg viewBox="0 0 24 24">
              <path d="M14 5v4H4v6h10v4l8-7-8-7z" />
            </svg>
            <span className="tb-btn-tooltip">
              Weiterleitung <kbd>⌘W</kbd>
            </span>
          </button>
          <button className="tb-hangup" onClick={endCall} title="Auflegen ⌘H">
            <svg viewBox="0 0 24 24">
              <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Welle 5: Snapshot-Viewer */}
      {snapshotViewerUrl && (
        <div
          onClick={() => setSnapshotViewerUrl(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexDirection: 'column', gap: 16, padding: 24,
          }}
        >
          {snapshotViewerMime.startsWith('image/') ? (
            <img
              src={snapshotViewerUrl}
              alt="Snapshot"
              style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain', borderRadius: 4 }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : snapshotViewerMime.includes('pdf') ? (
            <iframe
              src={snapshotViewerUrl}
              title={snapshotViewerName || 'PDF'}
              style={{ width: '90vw', height: '80vh', border: 'none', borderRadius: 4, background: '#fff' }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div onClick={(e) => e.stopPropagation()} style={{ background: '#1b2b2f', color: '#fff', padding: 32, borderRadius: 8, textAlign: 'center', maxWidth: '90vw' }}>
              <div style={{ fontSize: 44, marginBottom: 8 }}>📄</div>
              <div style={{ fontWeight: 600, marginBottom: 4, wordBreak: 'break-all' }}>{snapshotViewerName || 'Empfangene Datei'}</div>
              <div style={{ opacity: 0.7, fontSize: 13 }}>{snapshotViewerMime || 'Datei'}</div>
              <a href={snapshotViewerUrl} download={snapshotViewerName || 'datei'} style={{ display: 'inline-block', marginTop: 14, color: '#7ee787', fontWeight: 600, textDecoration: 'underline' }}>Herunterladen</a>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }} onClick={(e) => e.stopPropagation()}>
            {scanSave.status === 'saved' && (
              <div style={{ color: '#7ee787', fontWeight: 600, fontSize: 14 }}>
                ✓ Dokument gespeichert{scanSave.folder ? ` im Ordner „${scanSave.folder}“` : ''} ·{' '}
                <a href={snapshotViewerUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>Dokument öffnen</a>
              </div>
            )}
            {scanSave.status === 'err' && <div style={{ color: '#f88', fontSize: 13 }}>{scanSave.err}</div>}
            {snapshotViewerMime.includes('pdf') && (
              <div style={{ fontSize: 13, color: '#cfe3e8' }}>
                {sealStatus === 'sealing' && 'Siegle Dokument …'}
                {pendingReview && sealStatus !== 'sealing' && (
                  <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ color: '#ffd479', fontWeight: 600 }}>Review: bitte Dokument (inkl. Protokollseite) prüfen</span>
                    <button onClick={() => { const p = pendingReview; setPendingReview(null); void sealAndFile(p.sigId, p.filename, p.buf).catch((e: any) => { setSealStatus('error'); setSealError(String(e?.message || e)); }); }}
                      style={{ border: 'none', background: '#e2001a', color: '#fff', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 12.5, fontWeight: 700 }}>Jetzt siegeln &amp; ablegen</button>
                  </span>
                )}
                {sealStatus === 'sealed' && <span style={{ color: '#7ee787', fontWeight: 600 }}>✓ Automatisch versiegelt{sealLabel ? ` · ${sealLabel}` : ''}</span>}
                {rtc.lastSignature?.identStatus === 'verified' && <span style={{ color: '#7ee787', marginLeft: 10 }}>✓ Identität bestätigt (Passkey)</span>}
                {rtc.lastSignature?.identStatus === 'unavailable' && <span style={{ color: '#ffb74d', marginLeft: 10 }}>⚠ Identifizierung am Gerät nicht verfügbar</span>}
                {rtc.lastSignature?.identStatus === 'declined' && <span style={{ color: '#ffb74d', marginLeft: 10 }}>⚠ Identifizierung nicht abgeschlossen</span>}
                {rtc.lastSignature?.otpCode && otpResults[rtc.lastSignature.sigId] === true && <span style={{ color: '#7ee787', marginLeft: 10 }}>✓ SMS-Code bestätigt</span>}
                {rtc.lastSignature?.otpCode && otpResults[rtc.lastSignature.sigId] === false && <span style={{ color: '#f88', marginLeft: 10 }}>✗ SMS-Code falsch/abgelaufen</span>}
                {sealStatus === 'error' && <span style={{ color: '#f88' }}>Siegeln fehlgeschlagen (Zertifikat/Passwort prüfen)</span>}
                {sealStatus === 'idle' && (sealLabel
                  ? <span style={{ opacity: 0.8 }}>Siegel-Zertifikat bereit: {sealLabel}</span>
                  : <span style={{ opacity: 0.8 }}>Kein Siegel-Zertifikat — Verwaltung in den Einstellungen (Ablage &amp; Ordner)</span>)}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => {
                  const isPdf = snapshotViewerMime.includes('pdf');
                  const fmt = rtc.lastScan?.format ?? 'jpeg';
                  const ext = isPdf ? 'pdf' : (fmt === 'jpeg' ? 'jpg' : fmt);
                  const mime = isPdf ? 'application/pdf' : `image/${fmt === 'jpg' ? 'jpeg' : fmt}`;
                  const t = new Date();
                  const baseName = isPdf ? (snapshotViewerName || `Dokument ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}.pdf`) : `Scan ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}.${ext}`;
                  void saveDocFromUrl({ url: snapshotViewerUrl, kind: isPdf ? 'signed' : 'scan', mime, baseName }, setScanSave);
                }}
                disabled={scanSave.status === 'saving'}
                style={{ padding: '10px 18px', background: '#fff', color: '#000', borderRadius: 4, border: 'none', fontWeight: 600, cursor: 'pointer', opacity: scanSave.status === 'saving' ? 0.6 : 1 }}
              >
                {scanSave.status === 'saving' ? 'Speichere …' : scanSave.status === 'saved' ? 'Erneut ablegen' : 'Im Ordner ablegen'}
              </button>
              <button
                onClick={() => {
                  const isPdf = snapshotViewerMime.includes('pdf');
                  const fmt = rtc.lastScan?.format ?? 'jpeg';
                  const ext = isPdf ? 'pdf' : (fmt === 'jpeg' ? 'jpg' : fmt);
                  const mime = isPdf ? 'application/pdf' : `image/${fmt === 'jpg' ? 'jpeg' : fmt}`;
                  const t = new Date();
                  const baseName = isPdf ? (snapshotViewerName || `Dokument ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}.pdf`) : `Scan ${t.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}.${ext}`;
                  void saveDocFromUrl({ url: snapshotViewerUrl, kind: isPdf ? 'signed' : 'scan', mime, baseName, forceDialog: true }, setScanSave);
                }}
                style={{ padding: '10px 18px', background: 'transparent', color: '#fff', borderRadius: 4, border: '1px solid #fff', fontWeight: 600, cursor: 'pointer' }}
                title="Ort frei wählen"
              >
                Speichern unter …
              </button>
              <button
                onClick={() => setSnapshotViewerUrl(null)}
                style={{ padding: '10px 18px', background: 'transparent', color: '#fff', borderRadius: 4, border: '1px solid rgba(255,255,255,0.4)', fontWeight: 600, cursor: 'pointer' }}
              >
                Schließen
              </button>
            </div>
          </div>
          {rtc.lastScan?.error && (
            <div style={{ color: '#f88' }}>Scan-Fehler: {rtc.lastScan.error}</div>
          )}
        </div>
      )}

      {/* Welle 8c-pre38: Signatur-Status. Während des Vorgangs Abbruch-Button (löst auf der
          Stele Fetch-Abbruch + /Sign/getkill aus). */}
      <SignInbox onSign={(f) => rtc.submitSignature(f)} canSign={true} />
      {rtc.lastSignature &&
        rtc.lastSignature.sigId !== sigToastDismissed &&
        (rtc.lastSignature.status === 'uploading' ||
          rtc.lastSignature.status === 'submitted' ||
          rtc.lastSignature.status === 'signing' ||
          rtc.lastSignature.status === 'aborted' ||
          rtc.lastSignature.status === 'error') && (
          <div
            style={{
              position: 'fixed', bottom: 96, left: '50%', transform: 'translateX(-50%)',
              zIndex: 1200, padding: '12px 20px', borderRadius: 10,
              background: rtc.lastSignature.status === 'error' || rtc.lastSignature.status === 'aborted'
                ? 'rgba(180,40,40,0.95)' : 'rgba(20,20,26,0.95)',
              color: '#fff', fontWeight: 600, boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', gap: 14,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 240 }}>
              <span>
                {rtc.lastSignature.status === 'uploading' && `Dokument wird übertragen… ${rtc.lastSignature.progress ?? 0}%`}
                {rtc.lastSignature.status === 'submitted' && 'Dokument gesendet – warte auf Unterschrift des Kunden…'}
                {rtc.lastSignature.status === 'signing' && (typeof rtc.lastSignature.progress === 'number'
                  ? `Unterschriebenes Dokument wird übertragen… ${rtc.lastSignature.progress}%`
                  : 'Kunde unterschreibt am Display…')}
                {rtc.lastSignature.status === 'aborted' && 'Signiervorgang abgebrochen.'}
                {rtc.lastSignature.status === 'error' && `Signatur-Fehler: ${rtc.lastSignature.error ?? 'unbekannt'}`}
              </span>
              {typeof rtc.lastSignature.progress === 'number' &&
                (rtc.lastSignature.status === 'uploading' || rtc.lastSignature.status === 'signing') && (
                  <div style={{ height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${rtc.lastSignature.progress}%`, background: '#fff', transition: 'width .15s' }} />
                  </div>
                )}
            </div>
            {(rtc.lastSignature.status === 'uploading' ||
              rtc.lastSignature.status === 'submitted' ||
              rtc.lastSignature.status === 'signing') && (
              <button
                onClick={() => rtc.abortSignature()}
                style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}
              >
                Abbrechen
              </button>
            )}
            {(rtc.lastSignature.status === 'aborted' || rtc.lastSignature.status === 'error') && (
              <button
                onClick={() => setSigToastDismissed(rtc.lastSignature!.sigId)}
                style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 6, padding: '4px 12px', cursor: 'pointer' }}
              >
                OK
              </button>
            )}
          </div>
        )}

      {/* Welle 8c-pre38: Signiertes PDF anzeigen (liegt bereits in der Session-Ablage) */}
      {rtc.lastSignature?.status === 'received' &&
        rtc.lastSignature.url &&
        rtc.lastSignature.sigId !== sigViewerDismissed &&
        (!sealLabel || sealStatus === 'error') && (
          <div
            onClick={() => setSigViewerDismissed(rtc.lastSignature!.sigId)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)',
              zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: 16, padding: 24,
            }}
          >
            <div style={{ color: '#fff', fontWeight: 600, fontSize: 18 }}>
              Signiertes Dokument{rtc.lastSignature.filename ? ` – ${rtc.lastSignature.filename}` : ''}
            </div>
            <iframe
              src={rtc.lastSignature.url}
              title="Signiertes PDF"
              style={{ width: '80vw', height: '70vh', background: '#fff', border: 'none', borderRadius: 6 }}
              onClick={(e) => e.stopPropagation()}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
              {sigSave.status === 'saved' && (
                <div style={{ color: '#7ee787', fontWeight: 600, fontSize: 14 }}>
                  ✓ Dokument gespeichert{sigSave.folder ? ` im Ordner „${sigSave.folder}“` : ''} ·{' '}
                  <a href={rtc.lastSignature.url} target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'underline' }}>Dokument öffnen</a>
                </div>
              )}
              {sigSave.status === 'err' && <div style={{ color: '#f88', fontSize: 13 }}>{sigSave.err}</div>}
              {sigSave.status === 'idle' && <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 13 }}>In lokaler Ablage gesichert — bitte prüfen und ablegen.</span>}
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  onClick={() => void saveDocFromUrl({ url: rtc.lastSignature!.url!, kind: 'signed', mime: 'application/pdf', baseName: rtc.lastSignature!.filename ?? 'Signiertes Dokument.pdf' }, setSigSave)}
                  disabled={sigSave.status === 'saving'}
                  style={{ padding: '10px 18px', background: '#fff', color: '#000', borderRadius: 4, border: 'none', fontWeight: 600, cursor: 'pointer', opacity: sigSave.status === 'saving' ? 0.6 : 1 }}
                >
                  {sigSave.status === 'saving' ? 'Speichere …' : sigSave.status === 'saved' ? 'Erneut ablegen' : 'Im Ordner ablegen'}
                </button>
                <button
                  onClick={() => void saveDocFromUrl({ url: rtc.lastSignature!.url!, kind: 'signed', mime: 'application/pdf', baseName: rtc.lastSignature!.filename ?? 'Signiertes Dokument.pdf', forceDialog: true }, setSigSave)}
                  style={{ padding: '10px 18px', background: 'transparent', color: '#fff', borderRadius: 4, border: '1px solid #fff', fontWeight: 600, cursor: 'pointer' }}
                  title="Ort frei wählen"
                >
                  Speichern unter …
                </button>
                <button
                  onClick={() => setSigViewerDismissed(rtc.lastSignature!.sigId)}
                  style={{ padding: '10px 18px', background: 'transparent', color: '#fff', borderRadius: 4, border: '1px solid rgba(255,255,255,0.4)', fontWeight: 600, cursor: 'pointer' }}
                >
                  Schließen
                </button>
              </div>
            </div>
          </div>
        )}

      {selfPreviewOpen && (
        <SelfPreviewModal stream={rtc.localPortraitStream} onClose={() => setSelfPreviewOpen(false)} />
      )}

      <RecordingConsentModal
        open={consentOpen}
        phase={consentPhase}
        topic={consentTopic}
        topics={enabledTopics(recPolicy)}
        onSelectTopic={(id) => setConsentTopic(id as any)}
        onRequestConsent={() => void requestConsentFromStele()}
        onCancel={closeConsentModal}
        onCancelWaiting={cancelConsentWaiting}
        onClose={closeConsentModal}
      />
      {/* pre224: Berater platziert Unterschriftsfelder + Unterzeichner, dann Versand */}
      {placement && (
        <ErrorBoundary onError={() => setPlacement(null)} fallback={null}>
          <Suspense fallback={<div style={{ position: 'fixed', inset: 0, background: 'rgba(6,20,24,0.96)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#cfe3e8' }}>Dokument wird geladen …</div>}>
            <SignPlacementModal
              pdfBytes={placement.bytes}
              filename={placement.filename}
              onCancel={() => setPlacement(null)}
              onConfirm={(signers: Signer[], markers: SignMarker[], requireIdent: boolean, captureId: boolean, requireOtp: boolean) => {
                const f = placement.file; setPlacement(null); setSigViewerDismissed(null); setSigToastDismissed(null);
                void (async () => {
                  let challengeId: string | null = null;
                  if (requireOtp) {
                    const phone = window.prompt('Mobilnummer des Kunden für den SMS-Code (aus dem Banksystem):');
                    if (!phone) { window.alert('Ohne Mobilnummer kein SMS-Code — Vorgang abgebrochen.'); return; }
                    try {
                      const r = await apiFetch('/api/sms-otp/send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ phone }) });
                      const j = await r.json().catch(() => ({}));
                      if (!r.ok) { window.alert(`SMS-Versand fehlgeschlagen: ${j?.message || r.status}`); return; }
                      challengeId = j.challengeId;
                      if (j.devCode) window.alert(`DEBUG-Provider aktiv — Code (nur Testbetrieb): ${j.devCode}`);
                    } catch { window.alert('SMS-Versand fehlgeschlagen (Verbindung).'); return; }
                  }
                  const sigId = await rtc.submitSignature(f, markers, signers, requireIdent, requireOtp);
                  if (sigId && challengeId) otpChallengeBySigRef.current.set(sigId, challengeId);
                  if (sigId && captureId) setAwaitingIdCapture({ sigId, base: f.name.replace(/\.pdf$/i, '') });
                })();
              }}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </div>
  );
}
